// Centroid-driven recommendation tile — Session 7 PR2.
//
// Two empty states:
//   - `insufficient_data` (no progressed apps) → "Sharpen this list by
//     landing an interview." Copy is part of acceptance, per the PR brief.
//   - centroid present but no candidates → unlikely (would mean every
//     similar job is already tracked); render a small "all caught up".
//
// Non-empty: top 3 jobs with score pill + Track button. Shape mirrors
// TodaysShortlist so the two tiles read consistently.

import { ExternalLink, Plus, Sparkles } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { EmptyState } from "../ui/EmptyState";
import { Skeleton } from "../ui/Skeleton";
import { ScorePill } from "../jobs/ScorePill";
import { useCreateApplication } from "../../lib/applications";
import {
  useRecommendations,
  type RecommendationsResponse,
} from "../../lib/recommend";
import type { JobRow } from "../../lib/jobs";

export function RecommendTile() {
  const recommend = useRecommendations(3);
  const coldStart = recommend.data?.source === "cold_start";

  return (
    <Card>
      <CardHeader
        title="Your search is converging on this kind of role"
        description={
          coldStart
            ? "Based on your resume + career profile — no interviews needed yet. This sharpens once an application progresses past `applied`."
            : "Built from the jobs that actually moved past `applied` for you. Updates as more applications progress."
        }
        action={
          coldStart ? (
            <span className="rounded-full border border-accent-700/60 bg-accent-900/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-accent-300">
              from your profile
            </span>
          ) : null
        }
      />
      <CardBody>
        {recommend.isLoading ? (
          <RecommendSkeleton />
        ) : recommend.error ? (
          <EmptyState
            tone="danger"
            title="Couldn't load recommendations"
            description="Try refreshing in a moment."
          />
        ) : recommend.data?.reason === "insufficient_data" ? (
          <EmptyState
            icon={<Sparkles size={16} aria-hidden />}
            title="Sharper recommendations unlock once you land an interview"
            description="The centroid averages JDs from applications that progressed past `applied`. With 0 of those today, every job would tie — so we keep this empty rather than show noise."
          />
        ) : !recommend.data || recommend.data.returned === 0 ? (
          <EmptyState
            title="Caught up"
            description="Every match is already being tracked. Check /jobs for the full list."
          />
        ) : (
          <ul className="divide-y divide-neutral-800">
            {recommend.data.jobs.map((job) => (
              <RecommendRow key={`${job.company_id}:${job.job_id}`} job={job} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function RecommendSkeleton() {
  return (
    <ul className="divide-y divide-neutral-800">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <Skeleton className="h-6 w-12" />
        </li>
      ))}
    </ul>
  );
}

function RecommendRow({ job }: { job: JobRow }) {
  const qc = useQueryClient();
  const create = useCreateApplication();

  function trackInterested() {
    create.mutate(
      {
        url: job.url,
        role: job.title,
        company_id: job.company_id,
        status: "interested",
      },
      {
        onSuccess: (data) => {
          if (data.already_existed) return;
          // Drop the just-tracked row — server cache is 5min, this keeps
          // the tile honest without a manual refresh.
          qc.setQueryData<RecommendationsResponse>(
            ["recommend", 3],
            (prev) => {
              if (!prev) return prev;
              const remaining = prev.jobs.filter((j) => j.url !== job.url);
              return {
                ...prev,
                jobs: remaining,
                returned: remaining.length,
              };
            },
          );
        },
      },
    );
  }

  return (
    <li className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          {job.company_name}
        </p>
        <p className="mt-0.5 truncate text-sm font-medium text-neutral-100">
          {job.title}
        </p>
        {job.location_summary ? (
          <p className="mt-0.5 truncate text-xs text-neutral-500">
            {job.location_summary}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 sm:shrink-0">
        <ScorePill score={job.score} />
        <a
          href={job.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-neutral-700 bg-neutral-800/40 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600 hover:bg-neutral-800"
        >
          Open
          <ExternalLink size={12} aria-hidden />
        </a>
        <Button
          variant="secondary"
          size="sm"
          onClick={trackInterested}
          loading={create.isPending}
          leadingIcon={<Plus size={12} aria-hidden />}
        >
          Track
        </Button>
      </div>
    </li>
  );
}
